import React from 'react';
import { Palette, ArrowLeft } from 'lucide-react';
import type { Workflow, Project } from '../types';
import { CanvasGallery } from '../components/home/canvas/CanvasGallery';
import { CreateBanner } from '../components/home/canvas/CreateBanner';
import { HomeLayoutShell } from '../components/home/layout/HomeLayoutShell';

interface CanvasPageProps {
  workflows: Workflow[];
  projects?: Project[];
  onSelectWorkflow: (id: string) => void;
  onCreateProject: () => void;
  onDeleteWorkflow: (id: string) => void;
  onRenameWorkflow: (id: string, newTitle: string) => void;
  onBack?: () => void;
}

/**
 * 所有画布内页——与首页同步的即梦式流式壳：
 * - 顶部用户栏由 AppNew 统一挂载（本页 pt-24 让位 72px 顶栏），不再是无顶栏的孤立页
 * - 背景与全站一致 #121212（原 #141414 为旧占位风格）
 * - 内容壳复用首页 HomeLayoutShell（min 509 / max 1488 + px-6，流式自适应）；
 *   标题行与画廊统一约束在首页内容列节奏 max-w-[1200px]（与首页 AgentInputBox /
 *   CanvasGallery 同列，设计稿 141:2 内容列 1200）——否则标题行随壳层伸展至最宽
 *   1440，而画廊限宽居中，宽视口下两者左缘错位，偏离首页「画廊相关元素同列
 *   对齐」的断点行为。
 *   图标配色统一为全站中性风格
 */
export const CanvasPage: React.FC<CanvasPageProps> = ({
  workflows,
  projects,
  onSelectWorkflow,
  onCreateProject,
  onDeleteWorkflow,
  onRenameWorkflow,
  onBack,
}) => {
  return (
    <div className="w-full min-h-screen pt-24 pb-16" style={{ backgroundColor: '#121212' }}>
      <HomeLayoutShell>
        {/* 首页内容列节奏 1200px（AgentInputBox / CanvasGallery 同列）：
            标题行与画廊左缘在任意视口宽度下保持对齐 */}
        <div className="relative w-full max-w-[1200px] mx-auto">
          <div className="flex items-center gap-4 mb-8">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 rounded-full bg-white/5 border border-white/10 text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Palette size={20} className="text-zinc-200" />
              </div>
              <h1 className="text-2xl font-semibold text-white">所有画布</h1>
            </div>
          </div>

          {/* 创作页活动横幅（运营化）：运维平台轮播图 position=create 下发，
              无上线活动 / 拉取失败时整块隐藏，不占位 */}
          <CreateBanner />

          <CanvasGallery
            workflows={workflows}
            projects={projects}
            onSelectWorkflow={onSelectWorkflow}
            onCreateProject={onCreateProject}
            onDeleteWorkflow={onDeleteWorkflow}
            onRenameWorkflow={onRenameWorkflow}
          />
        </div>
      </HomeLayoutShell>
    </div>
  );
};
